// 订阅版本
export interface Plan {
  id: string;
  code: string;
  name: string;
  description: string;
  price_monthly: number;
  price_annually: number;
  max_seats: number;
  features: string;
  quotas: string;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

// 带当前状态的版本
export interface PlanWithQuota extends Plan {
  is_current: boolean;
}

// 订阅记录
export interface Subscription {
  id: string;
  tenant_id: string;
  plan_id: string;
  plan?: Plan;
  billing_cycle: 'monthly' | 'annually';
  price: number;
  start_date: string;
  end_date: string;
  auto_renew: boolean;
  status: 'active' | 'trial' | 'expired' | 'cancelled';
  payment_method?: string;
  trial_end?: string;
  is_trialing: boolean;
  created_at: string;
  updated_at: string;
}

// 用量汇总
export interface UsageSummary {
  metric_type: string;
  display_name: string;
  used: number;
  limit: number;
  percentage: number;
  is_unlimited: boolean;
}

// 配额检查结果
export interface QuotaCheckResult {
  allowed: boolean;
  used: number;
  limit: number;
  is_unlimited: boolean;
}

// 升级请求
export interface UpgradeRequest {
  plan_id: string;
  billing_cycle?: 'monthly' | 'annually';
}

// 降级请求
export interface DowngradeRequest {
  plan_id: string;
}

// 记录用量请求
export interface RecordUsageRequest {
  tenant_id: string;
  metric_type: string;
  count: number;
}

// 解析后的版本功能
export interface ParsedPlanFeatures {
  proposal_agent?: boolean;
  scout_agent?: boolean;
  outreach_agent?: boolean;
  ppt_generation?: boolean;
  whatsapp?: boolean;
  team_management?: boolean;
  api_access?: boolean;
  private_deployment?: boolean;
}

// 解析后的版本配额
export interface ParsedPlanQuotas {
  requirement_analysis?: number;
  product_insight?: number;
  pricing?: number;
  reference_influencer?: number;
  proposal_excel?: number;
  proposal_ppt?: number;
  influencer_search?: number;
  influencer_evaluate?: number;
  influencer_insight?: number;
  outreach_generate?: number;
  email_send?: number;
  storage_gb?: number;
  [key: string]: number | undefined;
}

// 版本代码常量
export const PlanCode = {
  TRIAL: 'trial',
  STARTER: 'starter',
  PROFESSIONAL: 'professional',
  ENTERPRISE: 'enterprise',
} as const;

// 订阅状态常量
export const SubscriptionStatus = {
  ACTIVE: 'active',
  TRIAL: 'trial',
  EXPIRED: 'expired',
  CANCELLED: 'cancelled',
} as const;

// 指标显示名称
export const MetricDisplayNames: Record<string, string> = {
  requirement_analysis: '需求解构',
  product_insight: '产品洞察',
  pricing: '报价方案',
  reference_influencer: '参考红人',
  proposal_excel: 'Excel提案',
  proposal_ppt: 'PPT提案',
  influencer_search: '红人搜索',
  influencer_evaluate: '红人评估',
  influencer_insight: '红人洞察',
  outreach_generate: '邀约生成',
  email_send: '邮件发送',
};

// 解析 JSON 字符串为对象
export function parseFeatures(featuresJson: string): ParsedPlanFeatures {
  try {
    return JSON.parse(featuresJson);
  } catch {
    return {};
  }
}

export function parseQuotas(quotasJson: string): ParsedPlanQuotas {
  try {
    return JSON.parse(quotasJson);
  } catch {
    return {};
  }
}

// 账单/发票
export interface Invoice {
  id: string;
  tenant_id: string;
  invoice_no: string;
  billing_month: string;
  subscription_fee: number;
  overage_fee: number;
  addon_fee: number;
  discount_amount: number;
  total_amount: number;
  status: 'pending' | 'paid' | 'failed' | 'refunded';
  paid_at?: string;
  invoice_url?: string;
  created_at: string;
  updated_at: string;
}

// 支付订单
export interface PaymentOrder {
  order_no: string;
  tenant_id: string;
  amount: number;
  subject: string;
  payment_method: 'alipay' | 'wechat' | 'bank_transfer';
  status: 'pending' | 'paid' | 'failed' | 'cancelled';
  pay_url?: string;
  created_at: string;
}

// 支付方式选项
export type PaymentMethod = 'alipay' | 'wechat' | 'bank_transfer';
